"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Comedian } from "@/data/comedians";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { UserComedianFollow } from "@/data/v2/client/user/follows";
import dayjs from "@punchup/utils/dayjs";
import { followComedianById } from "@punchup/data/client";
import { logException } from "@/analytics/sentry";
import { useUser } from "@punchup/data/legacy/hooks";

interface FollowComedianParams {
  comedian: Pick<Comedian, "id">;
  followLocation: FOLLOW_LOCATIONS;
  ctaSource?: string;
  pageSection?: string;
}

/**
 * Custom hook for following a comedian using React Query mutations
 *
 * Handles optimistic updates and automatic cache invalidation for user follows.
 * Uses the v2 API endpoint for following comedians.
 *
 * @returns Object containing:
 * - followComedian: Mutation function to follow a comedian
 * - isFollowing: Whether the follow mutation is in progress
 * - error: Error object if mutation failed
 *
 * @example
 * ```tsx
 * const { followComedian: follow, isFollowing } = useFollowComedian();
 *
 * const handleFollow = () => {
 *   follow({
 *     comedian: { id: 'comedian-123' },
 *     followLocation: FOLLOW_LOCATIONS.COMEDIAN_LANDING_PAGE
 *   });
 * };
 * ```
 */
export function useFollowComedian() {
  const queryClient = useQueryClient();
  const user = useUser();

  const mutation = useMutation({
    mutationFn: async ({
      comedian,
      followLocation,
      ctaSource,
      pageSection,
    }: FollowComedianParams) => {
      return followComedianById(
        comedian.id,
        followLocation,
        ctaSource,
        pageSection
      );
    },
    onMutate: async ({ comedian }) => {
      await queryClient.cancelQueries({
        queryKey: ["user-comedian-follows", user?.id],
      });

      const previousFollows = queryClient.getQueryData<UserComedianFollow[]>([
        "user-comedian-follows",
        user?.id,
      ]);

      queryClient.setQueryData<UserComedianFollow[]>(
        ["user-comedian-follows", user?.id],
        (old) => {
          if (!old) return old;
          return [
            ...old,
            {
              user_id: user?.id ?? "",
              comedian_id: comedian.id,
              created_at: dayjs().toISOString(),
            },
          ];
        }
      );

      return { previousFollows };
    },
    onError: (error, variables, context) => {
      if (context?.previousFollows) {
        queryClient.setQueryData(
          ["user-comedian-follows", user?.id],
          context.previousFollows
        );
      }

      logException(error, {
        extra: {
          message: "Error following comedian",
          comedianId: variables.comedian.id,
          followLocation: variables.followLocation,
        },
      });
    },
    onSuccess: () => {
      // Analytics tracking is handled server-side in the v2 API
      queryClient.invalidateQueries({
        queryKey: ["user-comedian-follows"],
      });
      queryClient.invalidateQueries({
        queryKey: ["user-content-access"],
      });
    },
  });

  return {
    followComedian: mutation.mutate,
    followComedianAsync: mutation.mutateAsync,
    isFollowing: mutation.isPending,
    error: mutation.error,
  };
}
